<script setup lang="ts">
import { computed } from "vue";
import { responsiveClass, responsiveVariables } from "../../utilities/helpers";
import type { IStackProps } from "./VStack.types";

const props = withDefaults(defineProps<IStackProps>(), {
  gap: () => ({ xs: "16px", m: "20px" }),
});

const rootClass = computed(() => {
  return [
    ...responsiveClass("pl-stack_direction", props.direction || "column"),
    ...responsiveClass("pl-stack_align", props.align),
    ...responsiveClass("pl-stack_justify", props.justify),
    ...responsiveClass("pl-stack_wrap", props.wrap),
  ];
});

const rootVariables = computed(() => {
  return {
    ...responsiveVariables("--_g", props.gap),
    ...responsiveVariables("--_h", props.height),
  };
});
</script>

<template>
  <div class="pl-stack" :class="rootClass" :style="rootVariables">
    <slot />
  </div>
</template>

<style lang="postcss" scoped>
.pl-stack {
  --_g: 0;
  --_g-s: var(--_g);
  --_g-m: var(--_g-s);
  --_g-ml: var(--_g-m);
  --_g-l: var(--_g-ml);
  --_g-xl: var(--_g-l);
  --_h: auto;
  --_h-s: var(--_h);
  --_h-m: var(--_h-s);
  --_h-ml: var(--_h-m);
  --_h-l: var(--_h-ml);
  --_h-xl: var(--_h-l);
  --pl-stack-gap: var(--_g);
  --pl-stack-height: var(--_h);

  display: flex;
  gap: var(--pl-stack-gap);
  height: var(--pl-stack-height);

  & > :deep(*) {
    --_o: 0;
    --_o-s: var(--_o);
    --_o-m: var(--_o-s);
    --_o-ml: var(--_o-m);
    --_o-l: var(--_o-ml);
    --_o-xl: var(--_o-l);
    --pl-stack-item-order: var(--_o);

    order: var(--pl-stack-item-order);
  }

  &&_direction-column {
    flex-flow: column nowrap;
  }

  &&_direction-row {
    flex-flow: row wrap;
    align-items: flex-start;
  }

  &&_wrap-nowrap {
    flex-wrap: nowrap;
  }

  &&_wrap-wrap {
    flex-wrap: wrap;
  }

  &&_align-baseline {
    align-items: baseline;
  }

  &&_align-start {
    align-items: flex-start;
  }

  &&_align-center {
    align-items: center;
  }

  &&_align-end {
    align-items: flex-end;
  }

  &&_align-stretch {
    align-items: stretch;
  }

  &&_justify-start {
    justify-content: flex-start;
  }

  &&_justify-center {
    justify-content: center;
  }

  &&_justify-end {
    justify-content: flex-end;
  }

  &&_justify-stretch {
    justify-content: stretch;
  }
}

@each $breakpoint in s, m, ml, l, xl {
  @media (--pl-viewport-from-$(breakpoint)) {
    .pl-stack {
      --pl-stack-gap: var(--_g-$(breakpoint));
      --pl-stack-height: var(--_h-$(breakpoint));

      & > :deep(*) {
        --pl-stack-item-order: var(--_o-$(breakpoint));
      }

      &&_direction-column-$(breakpoint) {
        flex-direction: column;
      }

      &&_direction-row-$(breakpoint) {
        flex-direction: row;
      }

      &&_wrap-nowrap-$(breakpoint) {
        flex-wrap: nowrap;
      }

      &&_wrap-wrap-$(breakpoint) {
        flex-wrap: wrap;
      }

      &&_align-baseline-$(breakpoint) {
        align-items: baseline;
      }

      &&_align-start-$(breakpoint) {
        align-items: flex-start;
      }

      &&_align-center-$(breakpoint) {
        align-items: center;
      }

      &&_align-end-$(breakpoint) {
        align-items: flex-end;
      }

      &&_align-stretch-$(breakpoint) {
        align-items: stretch;
      }

      &&_justify-start-$(breakpoint) {
        justify-content: flex-start;
      }

      &&_justify-center-$(breakpoint) {
        justify-content: center;
      }

      &&_justify-end-$(breakpoint) {
        justify-content: flex-end;
      }

      &&_justify-stretch-$(breakpoint) {
        justify-content: stretch;
      }
    }
  }
}
</style>
